import React, { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Trophy, Medal, Award, Copy, DollarSign } from 'lucide-react'
import { toast } from '@/hooks/use-toast'

// Draftactics Payout Preview - Deterministic, audit-safe prize distribution

// Helper: integer floor like SQL
const ifloor = (x: number) => Math.floor(x)

// Paid places: sqrt rule with 25% cap (configurable)
function paidPlaces(n: number, capFraction: number): number {
  if (n <= 0) return 0
  let p = Math.floor(Math.sqrt(n) + 0.5)
  p = Math.max(1, p)
  p = Math.min(p, Math.floor(capFraction * n))
  p = Math.max(p, 1)
  return p
}

// Sum of 1 / i^gamma, i in [1..p]
function weightSum(p: number, gamma: number): number {
  let s = 0
  for (let i = 1; i <= p; i++) s += 1 / Math.pow(i, gamma)
  return s
}

// Deterministic remainder distribution: cascade +1 from 1..P (looping if needed)
function distributeRemainder(base: number[], remainder: number): number[] {
  const out = [...base]
  if (remainder <= 0) return out
  let i = 0
  while (remainder > 0 && out.length > 0) {
    out[i] = out[i] + 1
    remainder -= 1
    i = (i + 1) % out.length
  }
  return out
}

interface PayoutRow {
  place: number
  vp: number
  pct: number
}

interface PayoutConfig {
  entrants: number
  fpPrice: number
  gamma: number
  payCapFraction: number
  vpPerDollar: number
  top1MinFraction: number
  top3MinFraction: number
  tailFloorFraction: number
  tailFloorMinVP: number
}

// Core payout builder (client mirror of SQL logic)
function buildPayouts(config: PayoutConfig) {
  const { entrants, fpPrice, gamma, payCapFraction, vpPerDollar, top1MinFraction, top3MinFraction, tailFloorFraction, tailFloorMinVP } = config
  
  const n = Math.max(0, entrants | 0)
  const poolDollars = 0.5 * n * fpPrice // 50% segregation rule
  const poolVPFloat = poolDollars * vpPerDollar
  const poolVP = ifloor(poolVPFloat)

  const P = paidPlaces(n, payCapFraction)
  if (P <= 0 || poolVP <= 0) return { poolDollars, poolVP, P: 0, rows: [] as PayoutRow[] }

  // Raw weights & shares
  const W = weightSum(P, gamma)
  const rawShares = Array.from({ length: P }, (_, i) => (1 / Math.pow(i + 1, gamma)) / W)
  let adjVPs = rawShares.map(s => s * poolVP)

  // Caps: Top1 minimum
  const top1Min = top1MinFraction * poolVP
  if (adjVPs[0] < top1Min) {
    const remainingPool = poolVP - top1Min
    const remShares = rawShares.slice(1).reduce((a, b) => a + b, 0)
    adjVPs[0] = top1Min
    for (let i = 1; i < P; i++) {
      adjVPs[i] = remainingPool * (rawShares[i] / (remShares || 1))
    }
  }

  // Caps: Top3 minimum combined
  if (P >= 3) {
    const top3Min = top3MinFraction * poolVP
    const top3Sum = adjVPs[0] + adjVPs[1] + adjVPs[2]
    if (top3Sum < top3Min) {
      const remainingPool = poolVP - top3Min
      if (top3Sum > 0) {
        adjVPs[0] = top3Min * (adjVPs[0] / top3Sum)
        adjVPs[1] = top3Min * (adjVPs[1] / top3Sum)
        adjVPs[2] = top3Min * (adjVPs[2] / top3Sum)
      } else {
        adjVPs[0] = top3Min * 0.60
        adjVPs[1] = top3Min * 0.25
        adjVPs[2] = top3Min * 0.15
      }

      const remShares = rawShares.slice(3).reduce((a, b) => a + b, 0)
      if (P > 3 && remShares > 0) {
        for (let i = 3; i < P; i++) {
          adjVPs[i] = remainingPool * (rawShares[i] / remShares)
        }
      }
    }
  }

  // Tail floor
  const tailFloor = Math.max(tailFloorMinVP, ifloor(tailFloorFraction * poolVP))
  for (let i = 0; i < P; i++) {
    if (adjVPs[i] < tailFloor) {
      adjVPs[i] = tailFloor
    }
  }

  // Floor to ints and distribute remainder deterministically 1..P
  const floorVPs = adjVPs.map(vp => ifloor(vp))
  const totalFloor = floorVPs.reduce((a, b) => a + b, 0)
  const remainder = poolVP - totalFloor
  const finalVPs = distributeRemainder(floorVPs, remainder)

  const rows: PayoutRow[] = finalVPs.map((vp, i) => ({
    place: i + 1,
    vp,
    pct: (vp / poolVP) * 100
  }))

  return { poolDollars, poolVP, P, rows }
}

interface PayoutPreviewProps {
  initialEntrants?: number
  initialFpPrice?: number
}

export function PayoutPreview({ initialEntrants = 100, initialFpPrice = 1.00 }: PayoutPreviewProps) {
  const [config, setConfig] = useState<PayoutConfig>({
    entrants: initialEntrants,
    fpPrice: initialFpPrice,
    gamma: 1.30,
    payCapFraction: 0.25,
    vpPerDollar: 1.0,
    top1MinFraction: 0.18,
    top3MinFraction: 0.45,
    tailFloorFraction: 0.002,
    tailFloorMinVP: 1
  })

  const payoutData = useMemo(() => buildPayouts(config), [config])

  const handleInputChange = (field: keyof PayoutConfig, value: number) => {
    setConfig(prev => ({ ...prev, [field]: value }))
  }

  const copyJsonToClipboard = () => {
    const exportData = {
      config,
      results: payoutData
    }
    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2))
    toast({
      title: 'Copied to clipboard',
      description: 'Payout configuration and results copied as JSON'
    })
  }

  const getRankIcon = (place: number) => {
    switch (place) {
      case 1: return <Trophy className="h-4 w-4 text-yellow-500" />
      case 2: return <Medal className="h-4 w-4 text-gray-400" />
      case 3: return <Award className="h-4 w-4 text-amber-600" />
      default: return <span className="text-sm font-medium">#{place}</span>
    }
  }

  return (
    <div className="space-y-6">
      {/* Configuration Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Deterministic Payout Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="entrants">Entrants</Label>
              <Input
                id="entrants"
                type="number"
                min={1}
                value={config.entrants}
                onChange={(e) => handleInputChange('entrants', parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="fpPrice">Field Pass ($)</Label>
              <Input
                id="fpPrice"
                type="number"
                step="0.01"
                min={0}
                value={config.fpPrice}
                onChange={(e) => handleInputChange('fpPrice', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="gamma">Gamma (γ)</Label>
              <Input
                id="gamma"
                type="number"
                step="0.01"
                min={1}
                value={config.gamma}
                onChange={(e) => handleInputChange('gamma', parseFloat(e.target.value) || 1.30)}
              />
            </div>
            <div>
              <Label htmlFor="payCapFraction">Pay Cap %</Label>
              <Input
                id="payCapFraction"
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={config.payCapFraction}
                onChange={(e) => handleInputChange('payCapFraction', parseFloat(e.target.value) || 0.25)}
              />
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-sm">
              <span className="font-medium">Pool:</span> ${payoutData.poolDollars.toFixed(2)} 
            </div>
            <div className="text-sm">
              <span className="font-medium">VP Pool:</span> {payoutData.poolVP.toLocaleString()} VP
            </div>
            <div className="text-sm">
              <span className="font-medium">Paid Places:</span> {payoutData.P} ({((payoutData.P / config.entrants) * 100).toFixed(1)}%)
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={copyJsonToClipboard}
              className="ml-auto"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy JSON
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payout Table */}
      <Card>
        <CardHeader>
          <CardTitle>Prize Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {payoutData.rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Place</TableHead>
                  <TableHead>VP Award</TableHead>
                  <TableHead>% of Pool</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payoutData.rows.map((row) => (
                  <TableRow key={row.place}>
                    <TableCell className="flex items-center gap-2">
                      {getRankIcon(row.place)}
                      <span className={row.place <= 3 ? 'font-semibold' : ''}>{row.place}</span>
                      {row.place <= 3 && (
                        <Badge variant={row.place === 1 ? 'default' : 'secondary'} className="text-xs">
                          {row.place === 1 ? 'Winner' : `Top ${row.place}`}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{row.vp.toLocaleString()} VP</TableCell>
                    <TableCell>{row.pct.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No payouts available for current configuration
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}